/**
 * React Query hook for managing per-user Recall method preference.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';

import { apiGet, apiPut } from '@/lib/http/client';

export type RecallMethodPreference =
  | 'auto'
  | 'bot_only'
  | 'sdk_only'
  | 'disabled';

export type MeetingScope = 'all' | 'external_only';

export interface RecallPreference {
  method_preference: RecallMethodPreference;
  meeting_scope: MeetingScope | null;
  exclusions: string[];
  bot_name: string;
  bot_avatar_url: string;
  consent_acknowledged_at: string | null;
}

export type UpdatablePreferences = {
  method_preference?: RecallMethodPreference;
  meeting_scope?: MeetingScope;
  exclusions?: string[];
  bot_name?: string;
  bot_avatar_url?: string;
  consent_acknowledged_at?: string;
};

export interface UseRecallPreferenceResult {
  preference: RecallPreference | null;
  loading: boolean;
  updating: boolean;
  error: string | null;
  available: boolean;
  setPreference: (value: UpdatablePreferences) => Promise<void>;
}

interface PreferenceResponse {
  method_preference: RecallMethodPreference;
  meeting_scope: MeetingScope | null;
  exclusions: string[] | null;
  bot_name: string | null;
  bot_avatar_url: string | null;
  consent_acknowledged_at: string | null;
}

interface ApiError extends Error {
  statusCode?: number;
}

export const preferenceKey = (orgId: number | undefined) =>
  ['recall', 'preference', orgId] as const;

export function useRecallPreference(
  orgId: number | undefined
): UseRecallPreferenceResult {
  const queryClient = useQueryClient();

  const query = useQuery<PreferenceResponse, ApiError>({
    queryKey: preferenceKey(orgId),
    queryFn: () =>
      apiGet<PreferenceResponse>(`/api/orgs/${orgId}/recall/preferences/me/`),
    enabled: !!orgId,
    retry: (count, error) => error?.statusCode !== 404 && count < 3,
  });

  // 404 = recall_notetaker flag disabled for this org; any other error keeps available=true
  const available =
    !!orgId && !(query.error && query.error?.statusCode === 404);

  useEffect(() => {
    if (query.error && query.error.statusCode !== 404) {
      console.error(query.error);
    }
  }, [query.error]);

  const mutation = useMutation<
    PreferenceResponse,
    ApiError,
    UpdatablePreferences
  >({
    mutationFn: (value: UpdatablePreferences) => {
      if (!orgId) {
        return Promise.reject(new Error('orgId is required'));
      }
      return apiPut<PreferenceResponse>(
        `/api/orgs/${orgId}/recall/preferences/me/`,
        value
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: preferenceKey(orgId) });
    },
  });

  const setPreference = useCallback(
    async (value: UpdatablePreferences): Promise<void> => {
      await mutation.mutateAsync(value);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- .mutateAsync is stable per React Query; the wrapper object isn't
    [mutation.mutateAsync]
  );

  const preference: RecallPreference | null = useMemo(
    () =>
      query.data
        ? {
            method_preference: query.data.method_preference,
            meeting_scope: query.data.meeting_scope,
            exclusions: query.data.exclusions ?? [],
            bot_name: query.data.bot_name ?? '',
            bot_avatar_url: query.data.bot_avatar_url ?? '',
            consent_acknowledged_at: query.data.consent_acknowledged_at,
          }
        : null,
    [query.data]
  );

  return {
    preference,
    loading: query.isLoading,
    updating: mutation.isPending,
    error:
      query.error && query.error.statusCode !== 404
        ? "We couldn't load the preference. Try again."
        : null,
    available,
    setPreference,
  };
}
